import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Users, Phone, Mail, Trash2, StickyNote, Loader2, Inbox, MessageCircle,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import DashLayout from "@/components/xuma/DashLayout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const STATUSES = [
  { value: "nuevo", label: "Nuevo", color: "#F4A261" },
  { value: "contactado", label: "Contactado", color: "#2563EB" },
  { value: "en_seguimiento", label: "En seguimiento", color: "#6D28D9" },
  { value: "cita", label: "Cita", color: "#0891B2" },
  { value: "ganado", label: "Ganado", color: "#16794D" },
  { value: "perdido", label: "Perdido", color: "#B42318" },
];
const statusMeta = (v) => STATUSES.find((s) => s.value === v) || STATUSES[0];
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "short" }); } catch { return ""; } };

export default function CRM() {
  const [leads, setLeads] = useState(null);
  const [filter, setFilter] = useState("todos");
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/leads").then(({ data }) => setLeads(data)).catch(() => setLeads([]));
  useEffect(() => { load(); }, []);

  const updateLead = async (id, patch) => {
    try {
      const { data } = await api.put(`/leads/${id}`, patch);
      setLeads((ls) => ls.map((l) => (l.lead_id === id ? data : l)));
      return data;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const removeLead = async (id) => {
    try {
      await api.delete(`/leads/${id}`);
      setLeads((ls) => ls.filter((l) => l.lead_id !== id));
      toast.success("Prospecto eliminado");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (leads === null) {
    return <DashLayout title="Mini CRM"><div className="grid place-items-center py-20"><Loader2 className="h-8 w-8 animate-spin text-xuma-primary" /></div></DashLayout>;
  }

  const counts = STATUSES.reduce((acc, s) => ({ ...acc, [s.value]: leads.filter((l) => l.status === s.value).length }), {});
  const shown = filter === "todos" ? leads : leads.filter((l) => l.status === filter);

  return (
    <DashLayout title="Mini CRM">
      {/* Pipeline summary */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {STATUSES.map((s) => (
          <button key={s.value} data-testid={`pipeline-${s.value}`} onClick={() => setFilter(filter === s.value ? "todos" : s.value)}
            className={`rounded-2xl border p-4 text-left transition-colors ${filter === s.value ? "border-xuma-primary bg-white" : "border-xuma-border bg-white hover:border-xuma-primary/40"}`}>
            <span className="h-2.5 w-2.5 rounded-full inline-block" style={{ background: s.color }} />
            <p className="mt-2 font-heading font-extrabold text-2xl text-xuma-dark">{counts[s.value]}</p>
            <p className="text-xs text-xuma-muted">{s.label}</p>
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <div className="rounded-3xl bg-white border border-xuma-border p-12 text-center">
          <Inbox className="h-10 w-10 mx-auto text-xuma-muted" />
          <h3 className="mt-4 font-heading font-bold text-xl text-xuma-dark">
            {filter === "todos" ? "Aún no tienes prospectos" : "Sin prospectos en este estado"}
          </h3>
          <p className="mt-2 text-sm text-xuma-muted">Los contactos del formulario de tu tarjeta aparecerán aquí.</p>
        </div>
      ) : (
        <div className="rounded-2xl bg-white border border-xuma-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="leads-table">
              <thead>
                <tr className="border-b border-xuma-border text-left text-xs uppercase tracking-wider text-xuma-muted">
                  <th className="px-4 py-3">Prospecto</th>
                  <th className="px-4 py-3 hidden md:table-cell">Interés</th>
                  <th className="px-4 py-3 hidden sm:table-cell">Fecha</th>
                  <th className="px-4 py-3">Estado</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {shown.map((l) => (
                  <tr key={l.lead_id} data-testid={`lead-row-${l.lead_id}`} className="border-b border-xuma-border last:border-0 hover:bg-xuma-dash/40">
                    <td className="px-4 py-3">
                      <p className="font-medium text-xuma-dark">{l.name}</p>
                      <div className="flex items-center gap-3 text-xs text-xuma-muted mt-0.5">
                        {l.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {l.phone}</span>}
                        {l.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {l.email}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-xuma-muted max-w-[180px] truncate">{l.service || "—"}</td>
                    <td className="px-4 py-3 hidden sm:table-cell text-xuma-muted">{fmtDate(l.created_at)}</td>
                    <td className="px-4 py-3">
                      <Select value={l.status} onValueChange={(v) => updateLead(l.lead_id, { status: v })}>
                        <SelectTrigger data-testid={`status-${l.lead_id}`} className="w-36 h-9">
                          <span className="flex items-center gap-2">
                            <span className="h-2 w-2 rounded-full" style={{ background: statusMeta(l.status).color }} />
                            <SelectValue />
                          </span>
                        </SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        <Button size="icon" variant="ghost" data-testid={`view-lead-${l.lead_id}`} onClick={() => setEditing(l)}>
                          <StickyNote className="h-4 w-4 text-xuma-muted" />
                        </Button>
                        <Button size="icon" variant="ghost" data-testid={`delete-lead-${l.lead_id}`} onClick={() => removeLead(l.lead_id)}>
                          <Trash2 className="h-4 w-4 text-xuma-muted" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-md">
          {editing && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">{editing.name}</DialogTitle>
                <DialogDescription>Detalle del prospecto y seguimiento.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <div className="flex flex-wrap gap-3 text-xuma-muted">
                  {editing.phone && <a href={`tel:${editing.phone}`} className="flex items-center gap-1.5 hover:text-xuma-dark"><Phone className="h-4 w-4" /> {editing.phone}</a>}
                  {editing.phone && <a href={`https://wa.me/${editing.phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 hover:text-green-600"><MessageCircle className="h-4 w-4" /> WhatsApp</a>}
                  {editing.email && <a href={`mailto:${editing.email}`} className="flex items-center gap-1.5 hover:text-xuma-dark"><Mail className="h-4 w-4" /> {editing.email}</a>}
                </div>
                {editing.service && <div><span className="text-xs uppercase tracking-wider text-xuma-muted">Interés</span><p className="text-xuma-dark">{editing.service}</p></div>}
                {editing.message && <div><span className="text-xs uppercase tracking-wider text-xuma-muted">Mensaje</span><p className="text-xuma-dark whitespace-pre-line">{editing.message}</p></div>}
                <div>
                  <Label className="mb-1.5 block">Notas / próxima acción</Label>
                  <Textarea data-testid="lead-notes" rows={4} defaultValue={editing.notes}
                    onChange={(e) => (editing._notes = e.target.value)} placeholder="Ej. Enviar cotización el lunes..." />
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="save-notes-btn" className="bg-xuma-primary hover:bg-xuma-primaryHover text-white"
                  onClick={async () => { await updateLead(editing.lead_id, { notes: editing._notes ?? editing.notes ?? "" }); toast.success("Nota guardada"); setEditing(null); }}>
                  Guardar nota
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </DashLayout>
  );
}
